import Editor from "@monaco-editor/react";
import { AlertTriangleIcon } from "lucide-react";
import { useTheme } from "next-themes";
import React from "react";
import { Focusable } from "react-aria-components";
import { useCopyToClipboard } from "react-use";

import { trpc } from "@blis/trpc/react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input, Label, TextField } from "@/components/ui/field";
import { Form } from "@/components/ui/form";
import { Modal } from "@/components/ui/modal";
import { Note } from "@/components/ui/note";
import { TableRAC } from "@/components/ui/table-rac";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import type { TInstrumentResultItem, TInstrumentResultSet } from "@/lib/types";
import { format } from "@/lib/utils";
import { useAccession } from "../AccessionProvider";
import { BaseTestResultBadge } from "./TestResultDetailsDialog";

const RESULT_TYPE_LABELS: Record<string, string> = {
  text: "Text",
  numeric: "Numeric",
  organism: "Organism",
  error: "Error",
  other: "Other",
};

const RESULT_TYPE_COLORS: Record<
  string,
  "gray" | "blue" | "green" | "red" | "amber"
> = {
  text: "blue",
  numeric: "green",
  organism: "blue",
  error: "red",
  other: "gray",
};

function ResultTypeBadge({ resultType }: { resultType: string | null }) {
  if (!resultType) return null;
  const label = RESULT_TYPE_LABELS[resultType] ?? resultType;
  const color = RESULT_TYPE_COLORS[resultType] ?? "gray";
  return (
    <Badge color={color} size="sm">
      {label}
    </Badge>
  );
}

function InstrumentResultValue({ item }: { item: TInstrumentResultItem }) {
  if (item.resultType === "error") {
    const errorText = [item.errorCode, item.errorMessage]
      .filter(Boolean)
      .join(": ");
    return (
      <TooltipTrigger delay={100}>
        <Focusable>
          <Badge color="red" size="sm">
            <AlertTriangleIcon className="size-3" />
            {item.errorCode || "Error"}
          </Badge>
        </Focusable>
        {item.errorMessage && (
          <Tooltip>
            <Text size="1">{errorText}</Text>
          </Tooltip>
        )}
      </TooltipTrigger>
    );
  }
  return (
    <span>
      {item.value} {item.units || ""}
    </span>
  );
}

export function InstrumentResultSetDialog({
  resultSet,
  children,
}: {
  resultSet: TInstrumentResultSet;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = React.useState(false);

  const { testOrders, samples } = useAccession();

  const allResults = React.useMemo(() => {
    function collect(
      orders: typeof testOrders,
    ): (typeof testOrders)[0]["results"] {
      return orders.flatMap((t) => [...t.results, ...collect(t.dependencies)]);
    }
    return collect(testOrders);
  }, [testOrders]);
  const matchedResults = resultSet.results
    .filter((r) => r.testOrderResultId !== null)
    .map((r) => ({
      ...r,
      testOrderResult: allResults.find((res) => res.id === r.testOrderResultId),
    }));

  const unmatchedResults = resultSet.results.filter(
    (r) => r.testOrderResultId === null,
  );

  const errorResults = resultSet.results.filter(
    (r) => r.resultType === "error",
  );

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      {children}
      <Modal size="4xl">
        <Dialog>
          <DialogHeader>
            <DialogTitle>Instrument result set</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="flex flex-col gap-4">
              {errorResults.length > 0 && (
                <Note intent="danger">
                  {errorResults.length} error
                  {errorResults.length !== 1 ? "s" : ""} reported by the
                  instrument.
                </Note>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
                <DetailItem title="Instrument">
                  {resultSet.instrument ? (
                    <Text>
                      {resultSet.instrument.model || resultSet.instrument.ref} (
                      {resultSet.instrument.manufacturer})
                    </Text>
                  ) : (
                    <Text muted>Unknown instrument</Text>
                  )}
                </DetailItem>

                <DetailItem title="Accession">
                  {resultSet.accessionId ? (
                    <Text>{resultSet.accessionId}</Text>
                  ) : (
                    <UpdateAccessionDialog>
                      <Button variant="ghost" size="sm">
                        Assign accession
                      </Button>
                    </UpdateAccessionDialog>
                  )}
                </DetailItem>

                <DetailItem title="Received">
                  {format(resultSet.createdAt)}
                </DetailItem>

                <DetailItem title="Operator">
                  {resultSet.operators.join(", ") || "-"}
                </DetailItem>
              </div>

              <DetailItem title="Matched results">
                {matchedResults.length > 0 ? (
                  <div className="border rounded-md overflow-hidden">
                    <TableRAC>
                      <TableRAC.Header className="&_th:py-1.5 *:text-xs">
                        <TableRAC.Row>
                          <TableRAC.Column width={160} maxWidth={160}>
                            Timestamp
                          </TableRAC.Column>
                          <TableRAC.Column isRowHeader>Test</TableRAC.Column>
                          <TableRAC.Column>Sample</TableRAC.Column>
                          <TableRAC.Column>Type</TableRAC.Column>
                          <TableRAC.Column>Result</TableRAC.Column>
                        </TableRAC.Row>
                      </TableRAC.Header>
                      <TableRAC.Body items={matchedResults}>
                        {(item) => (
                          <TableRAC.Row className="*:py-1.5 *:text-xs">
                            <TableRAC.Cell>
                              {format(item.resultDateTime)}
                            </TableRAC.Cell>
                            <TableRAC.Cell>
                              {item.testOrderResult?.testResultSpec.name ??
                                `[${item.testCode}] ${item.testName ?? ""}`}
                            </TableRAC.Cell>
                            <TableRAC.Cell>
                              {item.testOrderResult?.sample?.displayId ??
                                samples.find((s) => s.id === item.sampleId)
                                  ?.displayId ??
                                "\u2014"}
                            </TableRAC.Cell>
                            <TableRAC.Cell>
                              <ResultTypeBadge resultType={item.resultType} />
                            </TableRAC.Cell>
                            <TableRAC.Cell>
                              {item.testOrderResult ? (
                                <BaseTestResultBadge
                                  result={item.testOrderResult.result}
                                />
                              ) : (
                                <InstrumentResultValue item={item} />
                              )}
                            </TableRAC.Cell>
                          </TableRAC.Row>
                        )}
                      </TableRAC.Body>
                    </TableRAC>
                  </div>
                ) : (
                  <Text muted>No matched results</Text>
                )}
              </DetailItem>

              {unmatchedResults.length > 0 && (
                <DetailItem title="Unmatched results">
                  <div className="border rounded-md overflow-hidden">
                    <TableRAC>
                      <TableRAC.Header className="&_th:py-1.5 *:text-xs">
                        <TableRAC.Row>
                          <TableRAC.Column width={160} maxWidth={160}>
                            Timestamp
                          </TableRAC.Column>
                          <TableRAC.Column isRowHeader>Test</TableRAC.Column>
                          <TableRAC.Column>Sample</TableRAC.Column>
                          <TableRAC.Column>Type</TableRAC.Column>
                          <TableRAC.Column>Result</TableRAC.Column>
                        </TableRAC.Row>
                      </TableRAC.Header>
                      <TableRAC.Body items={unmatchedResults}>
                        {(item) => (
                          <TableRAC.Row className="*:py-1.5 *:text-xs">
                            <TableRAC.Cell>
                              {format(item.resultDateTime)}
                            </TableRAC.Cell>
                            <TableRAC.Cell>
                              [{item.testCode}] {item.testName}
                            </TableRAC.Cell>
                            <TableRAC.Cell>
                              {samples.find((s) => s.id === item.sampleId)
                                ?.displayId || "\u2014"}
                            </TableRAC.Cell>
                            <TableRAC.Cell>
                              <ResultTypeBadge resultType={item.resultType} />
                            </TableRAC.Cell>
                            <TableRAC.Cell>
                              <InstrumentResultValue item={item} />
                            </TableRAC.Cell>
                          </TableRAC.Row>
                        )}
                      </TableRAC.Body>
                    </TableRAC>
                  </div>
                </DetailItem>
              )}
            </div>
          </DialogBody>
          <DialogFooter className="sm:justify-end">
            <RawPayloadDialog resultSetId={resultSet.id}>
              <Button variant="ghost" size="sm">
                View raw payload
              </Button>
            </RawPayloadDialog>
          </DialogFooter>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}

function DetailItem({
  caption,
  title,
  children,
}: {
  title: string;
  caption?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label className="block pb-0.5">{title}</Label>
      {typeof children === "string" ? (
        <Text elementType="p" className="text-pretty text-muted-foreground">
          {children}
        </Text>
      ) : (
        children
      )}
      {caption && (
        <Text muted size="1">
          {caption}
        </Text>
      )}
    </div>
  );
}

function RawPayloadDialog({
  resultSetId,
  children,
}: {
  resultSetId: number;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [editorHeight, setEditorHeight] = React.useState("600px");
  const { theme } = useTheme();
  const [, copyToClipboard] = useCopyToClipboard();

  const { data: payload, isLoading } =
    trpc.instrumentResults.getPayload.useQuery(
      { id: resultSetId },
      { enabled: isOpen },
    );

  // Calculate responsive editor height
  React.useEffect(() => {
    if (!isOpen) return;

    const calculateHeight = () => {
      const viewportHeight = window.innerHeight;
      // For mobile (viewport < 640px), use 50% of viewport height
      // For larger screens, use 600px but cap at 70vh
      const isMobile = viewportHeight < 640;
      if (isMobile) {
        setEditorHeight(`${Math.floor(viewportHeight * 0.5)}px`);
      } else {
        const maxHeight = Math.floor(viewportHeight * 0.7);
        setEditorHeight(`${Math.min(600, maxHeight)}px`);
      }
    };

    calculateHeight();
    window.addEventListener("resize", calculateHeight);
    return () => window.removeEventListener("resize", calculateHeight);
  }, [isOpen]);

  const jsonString = React.useMemo(() => {
    if (!payload) return "";
    return JSON.stringify(payload, null, 2);
  }, [payload]);

  const handleCopy = React.useCallback(() => {
    if (jsonString) {
      copyToClipboard(jsonString);
      toast.success("Payload copied to clipboard");
    }
  }, [jsonString, copyToClipboard]);

  // Determine Monaco Editor theme based on app theme
  // "vs" is light theme, "vs-dark" is dark theme
  // Check document root class which is set by ThemeProvider
  const editorTheme = React.useMemo(() => {
    const isDark =
      theme === "dark" ||
      (theme === "system" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches) ||
      document.documentElement.classList.contains("dark");
    return isDark ? "vs-dark" : "vs";
  }, [theme]);

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      {children}
      <Modal size="4xl">
        <Dialog>
          <DialogHeader>
            <DialogTitle>Raw payload</DialogTitle>
          </DialogHeader>
          <DialogBody>
            {isLoading ? (
              <Text muted>Loading payload...</Text>
            ) : payload ? (
              <div className="flex flex-col gap-2">
                <Note intent="warning">
                  This payload is the full raw, data sent from the instrument.
                  It may contain orders and results from other accessions.
                </Note>
                <div className="border rounded-md overflow-hidden">
                  <Editor
                    language="json"
                    value={jsonString}
                    theme={editorTheme}
                    options={{
                      readOnly: true,
                      minimap: { enabled: false },
                      scrollBeyondLastLine: false,
                      fontSize: 12,
                      tabSize: 2,
                      insertSpaces: true,
                      automaticLayout: true,
                      wordWrap: "on",
                      lineNumbers: "on",
                    }}
                    height={editorHeight}
                  />
                </div>
              </div>
            ) : (
              <Text muted>No payload available</Text>
            )}
          </DialogBody>
          <DialogFooter className="sm:justify-between">
            <DialogCloseButton>Close</DialogCloseButton>
            {payload && (
              <Button variant="outline" onPress={handleCopy}>
                Copy
              </Button>
            )}
          </DialogFooter>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}

function UpdateAccessionDialog({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = React.useState(false);

  const onUpdateAccession = React.useCallback((data: FormData) => {
    const values = Object.fromEntries(data.entries());
    // TODO: not a use-case right now, but could be added later so keeping it here for now
  }, []);

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      {children}
      <Modal>
        <Dialog>
          <DialogHeader>
            <DialogTitle>Assign accession</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Note intent="info">
              Enter the accession ID that this result set belongs to.
            </Note>
            <Form id="update-accession-form" action={onUpdateAccession}>
              <TextField name="accessionId" isRequired>
                <Label>Accession ID</Label>
                <Input type="number" />
              </TextField>
            </Form>
          </DialogBody>
          <DialogFooter>
            <Button type="submit" form="update-accession-form">
              Update
            </Button>
            <DialogCloseButton>Cancel</DialogCloseButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    </DialogTrigger>
  );
}
